/**
 * Staff (teacher/manager) create/edit form.
 * Used in /admin/teachers list page dialog.
 * Fields: staffCode, fullName, role, branchId, specialization, phone, email.
 * Email is locked on edit (account-service owns it).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const staffFormSchema = z.object({
  staffCode: z.string().min(1, 'Bắt buộc').max(50),
  fullName: z.string().min(1, 'Bắt buộc').max(200),
  role: z.enum(['admin', 'manager', 'teacher']),
  branchId: z.string().min(1, 'Bắt buộc'),
  specialization: z.string().max(200).optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  email: z.string().email('Email không hợp lệ'),
});

export type StaffFormValues = z.infer<typeof staffFormSchema>;

interface BranchOption {
  id: string;
  name: string;
}

interface Props {
  isEdit: boolean;
  isSubmitting: boolean;
  branches: BranchOption[];
  /** Admin can set any role; manager can only set teacher */
  actorRole?: string;
  defaultValues?: Partial<StaffFormValues>;
  onSubmit: (values: StaffFormValues) => void;
  onCancel: () => void;
}

export function StaffForm({
  isEdit,
  isSubmitting,
  branches,
  actorRole,
  defaultValues,
  onSubmit,
  onCancel,
}: Props) {
  const form = useForm<StaffFormValues>({
    resolver: zodResolver(staffFormSchema),
    defaultValues: {
      staffCode: '',
      fullName: '',
      role: 'teacher',
      branchId: '',
      specialization: '',
      phone: '',
      email: '',
      ...defaultValues,
    },
  });

  const isAdmin = actorRole === 'admin';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Code — locked on edit */}
        <FormField
          control={form.control}
          name="staffCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mã nhân viên <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input {...field} disabled={isEdit} placeholder="VD: TC0001" className="font-mono" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Họ tên <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input {...field} placeholder="Họ và tên đầy đủ" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Role — admin can set any; manager locked to teacher */}
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vai trò <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={!isAdmin}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn vai trò" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {isAdmin && <SelectItem value="manager">Quản lý</SelectItem>}
                    <SelectItem value="teacher">Giáo viên</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Branch */}
          <FormField
            control={form.control}
            name="branchId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cơ sở <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={isEdit}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn cơ sở" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="specialization"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Chuyên môn</FormLabel>
              <FormControl>
                <Input {...field} placeholder="VD: Tiếng Anh giao tiếp, IELTS..." />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Số điện thoại</FormLabel>
                <FormControl>
                  <Input type="tel" {...field} placeholder="0912 345 678" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    {...field}
                    placeholder="email@example.com"
                    disabled={isEdit}
                  />
                </FormControl>
                {!isEdit && (
                  <p className="text-xs text-muted-foreground">
                    Hệ thống sẽ gửi email mời kích hoạt tài khoản.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? 'Đang lưu...'
              : isEdit
                ? 'Lưu thay đổi'
                : 'Tạo tài khoản'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
